import { Injectable } from '@angular/core';
import { ApiHttpClient } from '../../http/clients/api';
import { Observable } from 'rxjs';
import { PaginatedSms, Sms } from './sms.model';
import { NewSms } from './sms-new.model';
import { environment } from '../../../environments/environment';
import { CriteriaObject } from '../../models/dto/criteria-object.model';
import { TemplateVariable } from '../../messages/message-service/template-variable.model';
import { SmsTemplate } from 'app/models/dto/sms-template.model';
import { TodayCounts } from 'app/messages/message-service/message.service';

const unknownSmsRoute = '/sms/unknown-sms';
@Injectable({
  providedIn: 'root',
})
export class SmsService {

  defaultHistoryLimit = 15;

  constructor(private http: ApiHttpClient) {}

  // save an sms
  save(sms: NewSms): Observable<Sms> {
    return this.http.post<Sms>(`${environment.API_URL}/sms`, {
      message: sms.content,
      borrowerId: sms.borrowerId,
      toNumber: sms.toNumber,
      templateId: sms.templateId
    });
  }

  // gets all the sms history for a borrower by borrower id
  getBorrowerSmsHistory(borrowerId: number, retrieveAll = false, limit = null): Observable<Sms[]> {
    const queryObject: CriteriaObject = {
      criteria: {
        'sms.borrowerId': { '==': borrowerId }
      },
      sort: null,
      limit: null
    };

    if (!retrieveAll) {
      queryObject.sort = {
        'sms.created': 'DESC'
      };

      queryObject.limit = limit || this.defaultHistoryLimit;
    }

    const query = encodeURIComponent(JSON.stringify(queryObject));

    return this.http.get<Sms[]>(`${environment.API_URL}/sms?query=${query}`);
  }

  put(id: number, updates: any): Observable<Sms> {
    return this.http.put<Sms>(`${environment.API_URL}/sms/${id}`, updates);
  }

  getTemplateVariables(): Observable<TemplateVariable[]> {
    return this.http.get<TemplateVariable[]>(`${environment.API_URL}/sms/variables`);
  }

  getSmsVariables(borrowerId: number): Observable<any> {
    return this.http.get(`${environment.API_URL}/sms/variables/${borrowerId}`);
  }

  getSmsTemplates(): Observable<SmsTemplate[]> {
    return this.http.get<SmsTemplate[]>(`${environment.API_URL}/sms-template`);
  }

  deleteSmsTemplate(templateId: number): Observable<any> {
    return this.http.delete<any>(`${environment.API_URL}/sms-template/${templateId}`);
  }

  createSmsTemplate(template: { title: string, content: string }): Observable<SmsTemplate> {
    return this.http.post<SmsTemplate>(`${environment.API_URL}/sms-template`, template);
  }

  updateSmsTemplate(template: { id: number, title: string, content: string }): Observable<SmsTemplate> {
    return this.http.put<SmsTemplate>(`${environment.API_URL}/sms-template/${template.id}`, template);
  }

  getUnknownSms(lenderUserId: number, nextUrl: string | null): Observable<PaginatedSms> {
    const queryParams: CriteriaObject = {
      criteria: {
        'sms.borrowerId': { '==': 'null' },
        'sms.lenderUserId': { '==': lenderUserId },
        'sms.fromNumber': { '!=': '8015099589'},
        'sms.type': { '==': 'incoming'}
      }
    };
    const query = encodeURIComponent(JSON.stringify(queryParams));
    if (nextUrl) {
      return this.http.get<PaginatedSms>(`${environment.API_URL}${this.getRoute(nextUrl)}&query=${query}`);
    } else {
      return this.http.get<PaginatedSms>(`${environment.API_URL}${unknownSmsRoute}?query=${query}`);
    }
  }

  delete(smsId: number): Observable<any> {
    return this.http.delete<any>(`${environment.API_URL}/sms/${smsId}`);
  }

  getRoute(absoluteUrl) {
    return absoluteUrl.slice(absoluteUrl.indexOf(unknownSmsRoute));
  }

  getLenderUserSms(lenderUserId, page, filterBy, sortDirection): Observable<PaginatedSms> {
    const options = {
      params: {
        page,
        filterBy,
        sortDirection,
      }
    };
    return this.http.get<PaginatedSms>(`${environment.API_URL}/sms/${lenderUserId}`, options);
  }

  getLenderUserUnknownSms(lenderUserId, page, sortDirection): Observable<PaginatedSms> {
    const options = {
      params: {
        page,
        sortDirection,
      }
    };
    return this.http.get<PaginatedSms>(`${environment.API_URL}/sms/lenderUserUnknownSms/${lenderUserId}`, options);
  }

  markSmsRead(lenderUserId: number, borrowerId?: number, smsIds?: Array<number>) {
    return this.http.post<TodayCounts>(`${environment.API_URL}/bulk-sms-read`, {
      lenderUserId,
      borrowerId,
      smsIds,
    });
  }

  markSmsUnread(lenderUserId: number, borrowerId?: number, smsIds?: Array<number>) {
    return this.http.post<TodayCounts>(`${environment.API_URL}/sms/bulk-mark-unread`, {
      lenderUserId,
      borrowerId,
      smsIds,
    });
  }
}
